// ============================================================================
// firebase.js  (CLEANED – LinkFlower removed) 2025-08-28
// - no template literals in assertRequired
// - EVERY Firestore path goes through safeDoc/safeCollection to avoid undefined.path
// 初始化 + Auth + 通用 + Chat(TTL) + Screenshots/Collections + Playlists/Tracks
// + Newsroom + Open Calendar
// NOTE: Newsroom + Screenshot helpers below are kept as dead exports — the
// feature folders were removed but BurnerChat still imports uploadScreenshotFile
// from this file, so the screenshot section must stay in place.
// ============================================================================

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  inMemoryPersistence,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

/* ────────────────────────────────────────────── Utils */
function assertRequired(name, v) {
  if (v === undefined || v === null || v === '') {
    throw new Error(name + ' is required');
  }
  return v;
}

function _checkSegs(kind, segs) {
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    if (s === undefined || s === null || s === '') {
      throw new Error(kind + ': path segment[' + i + '] is ' + s);
    }
  }
}
function safeDoc() {
  const args = Array.prototype.slice.call(arguments);
  const dbOrRef = args.shift();
  _checkSegs('safeDoc', [dbOrRef].concat(args));
  return doc.apply(null, [dbOrRef].concat(args));
}
function safeCollection() {
  const args = Array.prototype.slice.call(arguments);
  const dbOrRef = args.shift();
  _checkSegs('safeCollection', [dbOrRef].concat(args));
  return collection.apply(null, [dbOrRef].concat(args));
}
function safeAutoDoc(colRef) {
  if (!colRef) throw new Error('safeAutoDoc: colRef is ' + colRef);
  return doc(colRef);
}

/* ────────────────────────────────────────────── Firebase Config */
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/* ────────────────────────────────────────────── Bootstrap */
const defaultApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
// Firebase Auth defaults to IndexedDB persistence, which can HANG inside the
// Capacitor iOS WKWebView during init — and since Firestore waits for an auth
// token before any read, that hang freezes the whole app on "Loading…". Use
// localStorage (browserLocalPersistence) which is reliable in WKWebView.
let _auth;
try {
  _auth = initializeAuth(defaultApp, {
    persistence: [inMemoryPersistence],
  });
} catch (e) {
  // Already initialized (HMR / double import) — reuse it.
  _auth = getAuth(defaultApp);
}
export const auth = _auth;
try {
  let _asc = false, _itc = false;
  auth.onAuthStateChanged && auth.onAuthStateChanged((u) => {
    _asc = true; console.error("DBG onAuthStateChanged fired user=" + (u ? u.uid : "null"));
  });
  auth.onIdTokenChanged && auth.onIdTokenChanged(() => { _itc = true; console.error("DBG onIdTokenChanged fired"); });
  setTimeout(() => {
    console.error("DBG auth@6s onAuthStateChanged=" + _asc + " onIdTokenChanged=" + _itc + " currentUser=" + (auth.currentUser ? auth.currentUser.uid : "null"));
  }, 6000);
} catch (e) { console.error("DBG auth probe threw " + (e && e.message)); }
// Firestore's default WebChannel transport HANGS inside the Capacitor iOS
// WKWebView (queries never resolve → the app gets stuck on "Loading…").
// `experimentalAutoDetectLongPolling` makes Firestore fall back to long
// polling in environments that need it (webviews) while still using the
// faster transport in normal browsers. Must run before any getFirestore().
let _db;
let _fsBranch;
try {
  _db = initializeFirestore(defaultApp, {
    experimentalForceLongPolling: true,
  });
  _fsBranch = "FORCE_LONGPOLL_OK";
} catch (e) {
  // Already initialized (e.g. dev HMR re-import) — reuse the instance.
  _db = getFirestore(defaultApp);
  _fsBranch = "FALLBACK_PLAIN: " + (e?.message || String(e));
}
console.error("DBG fs init:", _fsBranch);
export const db = _db;
export const storage = getStorage(defaultApp);
export const functions = getFunctions(defaultApp);

/* ────────────────────────────────────────────── Auth Helpers */
export const googleProvider = new GoogleAuthProvider();
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
export const googleSignIn = () => signInWithPopup(auth, googleProvider);
export const appleSignIn = () => signInWithPopup(auth, appleProvider);
export const googleSignOut = () => signOut(auth);
export const sendPasswordReset = email => sendPasswordResetEmail(auth, email);

/* ────────────────────────────────────────────── Generic User Helpers */
export const userDocRef = (uid = auth.currentUser && auth.currentUser.uid) =>
  uid ? safeDoc(db, 'users', uid) : null;

/* —— 我的用药：实时监听 + 新增/删除 —— */
export function listenMeds(uid, callback) {
  if (!uid) {
    console.error('listenMeds: uid missing');
    return () => {};
  }
  const q = query(
    safeCollection(db, 'users', uid, 'meds'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  });
}

export async function addMed(uid, med) {
  assertRequired('addMed.uid', uid);
  assertRequired('addMed.med.id', med.id);
  const ref = safeDoc(db, 'users', uid, 'meds', med.id);
  await setDoc(ref, { ...med, createdAt: serverTimestamp() });
  return med.id;
}

export async function deleteMed(uid, medId) {
  assertRequired('deleteMed.uid', uid);
  assertRequired('deleteMed.medId', medId);
  return deleteDoc(safeDoc(db, 'users', uid, 'meds', medId));
}

export async function getBuilderConfig(uid) {
  assertRequired('getBuilderConfig.uid', uid);
  const snap = await getDoc(safeDoc(db, 'users', uid));
  return snap.exists() ? (snap.data().builderConfig || null) : null;
}

export async function saveBuilderConfig(uid, builderConfig) {
  assertRequired('saveBuilderConfig.uid', uid);
  return setDoc(
    safeDoc(db, 'users', uid),
    { builderConfig: { ...builderConfig, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

export async function loadLcaData(uid = auth.currentUser && auth.currentUser.uid) {
  if (!uid) return null;
  const ref = userDocRef(uid);
  if (!ref) return null;
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  return data.lcaData || null;
}

export async function saveLcaData(data, uid = auth.currentUser && auth.currentUser.uid) {
  assertRequired('saveLcaData.uid', uid);
  return setDoc(
    userDocRef(uid),
    { lcaData: { ...data, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

export async function loadGmailAccounts(uid = auth.currentUser && auth.currentUser.uid) {
  if (!uid) return [];
  const snap = await getDoc(userDocRef(uid));
  return snap.exists() ? (snap.data().gmailAccounts || []) : [];
}

export async function saveGmailAccounts(accounts, uid = auth.currentUser && auth.currentUser.uid) {
  assertRequired('saveGmailAccounts.uid', uid);
  return setDoc(
    userDocRef(uid),
    { gmailAccounts: accounts, updatedAt: serverTimestamp() } ,
    { merge: true }
  );
}

/* ────────────────────────────────────────────── Generic Upload Helper */
export async function uploadAudio(blob, uidOverride) {
  assertRequired('uploadAudio.blob', blob);
  const uid = uidOverride || (auth.currentUser && auth.currentUser.uid) || 'public';
  const ext = (blob.type.split('/')[1] || 'webm').replace(/[^a-z0-9]/gi, '');
  const filename = Date.now() + '.' + ext;
  const sRef = storageRef(storage, 'lca-audio/' + uid + '/' + filename);
  await uploadBytes(sRef, blob);
  return getDownloadURL(sRef);
}

/* ─────────────────────────────── ⬇️ 新增：Avatar Upload Helpers ────────────────── */
/** 上传裁剪后的头像 Blob（WebP/PNG），返回下载 URL */
export async function uploadAvatarBlob(uid, blob) {
  assertRequired('uploadAvatarBlob.uid', uid);
  assertRequired('uploadAvatarBlob.blob', blob);
  // 统一命名：覆盖旧图，便于缓存 bust
  const ref = storageRef(storage, 'users/' + uid + '/avatar/cropped-512.webp');
  await uploadBytes(ref, blob, { contentType: blob.type || 'image/webp' });
  const url = await getDownloadURL(ref);
  return url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now(); // 防缓存
}

/** （可选）上传原图文件，返回下载 URL */
export async function uploadAvatarOriginal(uid, file) {
  assertRequired('uploadAvatarOriginal.uid', uid);
  assertRequired('uploadAvatarOriginal.file', file);
  const ext = (file.type.split('/')[1] || file.name.split('.').pop() || 'bin').replace(/[^a-z0-9]/gi, '');
  const ref = storageRef(storage, 'users/' + uid + '/avatar/original.' + ext);
  await uploadBytes(ref, file, { contentType: file.type || 'application/octet-stream' });
  return getDownloadURL(ref);
}
/* ─────────────────────────────── ⬆️ 新增结束 ────────────────── */

/* ─────────────────────────────── CHAT helpers (with TTL) ────────────────── */
export async function touchRoom(roomId) {
  assertRequired('touchRoom.roomId', roomId);
  await setDoc(
    safeDoc(db, 'rooms', roomId),
    { lastActive: serverTimestamp(), active: true, ttl: null },
    { merge: true }
  );
}

export async function sendChatMessage(roomId, obj) {
  assertRequired('sendChatMessage.roomId', roomId);
  const sender = obj && obj.sender;
  const text = obj && obj.text;
  const fileUrl = obj && obj.fileUrl;
  const colRef = safeCollection(db, 'rooms', roomId, 'messages');
  await addDoc(colRef, {
    sender: sender,
    text: text || null,
    fileUrl: fileUrl || null,
    createdAt: serverTimestamp(),
    ttl: Timestamp.fromMillis(Date.now() + 60 * 1000),
  });
  await touchRoom(roomId);
}

export function subscribeToChat(roomId, onUpdate) {
  if (!roomId) {
    console.error('subscribeToChat: roomId missing');
    return function () {};
  }
  const qMsgs = query(
    safeCollection(db, 'rooms', roomId, 'messages'),
    orderBy('createdAt')
  );
  return onSnapshot(qMsgs, snap => {
    const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onUpdate(msgs);
  });
}

export function deleteChatMessage(roomId, mid) {
  assertRequired('deleteChatMessage.roomId', roomId);
  assertRequired('deleteChatMessage.mid', mid);
  return deleteDoc(safeDoc(db, 'rooms', roomId, 'messages', mid));
}

export function watchParticipants(roomId) {
  if (!roomId) {
    console.error('watchParticipants: roomId missing');
    return function () {};
  }
  const colRef = safeCollection(db, 'rooms', roomId, 'participants');
  return onSnapshot(colRef, snap => {
    if (snap.size === 0) {
      setDoc(
        safeDoc(db, 'rooms', roomId),
        { active: false, ttl: Timestamp.fromMillis(Date.now() + 5 * 60 * 60 * 1000) },
        { merge: true }
      );
    }
  });
}

export async function destroyRoom(roomId) {
  assertRequired('destroyRoom.roomId', roomId);
  await setDoc(
    safeDoc(db, 'rooms', roomId),
    { active: false, ttl: Timestamp.now() },
    { merge: true }
  );
  try {
    const batch = writeBatch(db);
    const msgsSnap = await getDocs(safeCollection(db, 'rooms', roomId, 'messages'));
    msgsSnap.forEach(d => batch.delete(d.ref));
    const partsSnap = await getDocs(safeCollection(db, 'rooms', roomId, 'participants'));
    partsSnap.forEach(d => batch.delete(d.ref));
    await batch.commit();
  } catch (e) {
    console.warn('Cascade delete partly failed, TTL will handle rest', e);
  }
}

export async function deleteChatMessageWithFile(roomId, msgId) {
  assertRequired('deleteChatMessageWithFile.roomId', roomId);
  assertRequired('deleteChatMessageWithFile.msgId', msgId);

  const msgRef = safeDoc(db, 'rooms', roomId, 'messages', msgId);
  const snap = await getDoc(msgRef);
  const data = snap.data() || {};

  if (data.fileUrl) {
    try {
      const path = decodeURIComponent(data.fileUrl.split('/o/')[1].split('?')[0]);
      await deleteObject(storageRef(storage, path));
    } catch (e) {
      console.warn('Failed to delete file from Storage:', e);
    }
  }
  await deleteDoc(msgRef);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Screenshots & Collections
   ──────────────────────────────────────────────────────────────────────────────── */
export const screenshotsRef = uid => safeCollection(db, 'users', uid, 'screenshots');

export async function loadScreenshots(uid) {
  assertRequired('loadScreenshots.uid', uid);
  const snap = await getDocs(screenshotsRef(uid));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addScreenshot(uid, data) {
  assertRequired('addScreenshot.uid', uid);
  const ref = await addDoc(screenshotsRef(uid), { ...data, createdAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: ref.id, ...snap.data() };
}

export const collectionsRef = uid => safeCollection(db, 'users', uid, 'collections');

export async function loadCollections(uid) {
  assertRequired('loadCollections.uid', uid);
  const snap = await getDocs(collectionsRef(uid));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addCollection(uid, name) {
  assertRequired('addCollection.uid', uid);
  const ref = await addDoc(collectionsRef(uid), {
    name: name,
    ids: [],
    createdAt: serverTimestamp(),
  });
  const snap = await getDoc(ref);
  return { id: ref.id, ...snap.data() };
}

export async function moveToCollection(uid, colId, screenshotIds) {
  assertRequired('moveToCollection.uid', uid);
  assertRequired('moveToCollection.colId', colId);
  const colDoc = safeDoc(db, 'users', uid, 'collections', colId);
  await updateDoc(colDoc, { ids: screenshotIds, updatedAt: serverTimestamp() });
  const snap = await getDoc(colDoc);
  return { id: snap.id, ...snap.data() };
}

export async function uploadBlobFile(blob, path) {
  assertRequired('uploadBlobFile.blob', blob);
  assertRequired('uploadBlobFile.path', path);
  const ext = (blob.type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '');
  const filename = Date.now() + '.' + ext;
  const ref = storageRef(storage, path + '/' + filename);
  await uploadBytes(ref, blob);
  return getDownloadURL(ref);
}

export async function uploadScreenshotFile(uid, file) {
  assertRequired('uploadScreenshotFile.uid', uid);
  assertRequired('uploadScreenshotFile.file', file);
  const ext = (file.type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '');
  const filename = Date.now() + '.' + ext;
  const ref = storageRef(storage, 'users/' + uid + '/screenshots/' + filename);
  await uploadBytes(ref, file);
  return getDownloadURL(ref);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Playlists & Tracks
   ──────────────────────────────────────────────────────────────────────────────── */
export function listenPlaylists(uid, callback) {
  if (!uid) {
    console.error('listenPlaylists: uid is missing');
    return function () {};
  }
  const qPl = query(
    safeCollection(db, 'users', uid, 'playlists'),
    orderBy('order', 'asc')
  );
  return onSnapshot(qPl, snap => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  });
}

export function listenTracks(uid, playlistId, callback) {
  if (!uid) {
    console.error('listenTracks: uid is missing');
    return function () {};
  }
  if (!playlistId) {
    console.error('listenTracks: playlistId is missing');
    return function () {};
  }
  const qTr = query(
    safeCollection(db, 'users', uid, 'playlists', playlistId, 'tracks'),
    orderBy('order', 'asc')
  );
  return onSnapshot(qTr, snap => {
    const tracks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(tracks);
  });
}

export async function addPlaylist(uid, obj) {
  assertRequired('addPlaylist.uid', uid);
  const name = obj && obj.name;
  const slug = obj && obj.slug ? obj.slug : null;
  const col = safeCollection(db, 'users', uid, 'playlists');
  const snap = await getDocs(query(col, orderBy('order', 'desc')));
  const maxOrder = snap.docs.length ? (snap.docs[0].data().order || 0) : -1;
  const ref = await addDoc(col, {
    name: name,
    slug: slug,
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function addTrack(uid, playlistId, trackData) {
  assertRequired('addTrack.uid', uid);
  assertRequired('addTrack.playlistId', playlistId);
  const col = safeCollection(db, 'users', uid, 'playlists', playlistId, 'tracks');
  const snap = await getDocs(query(col, orderBy('order', 'desc')));
  const maxOrder = snap.docs.length ? (snap.docs[0].data().order || 0) : -1;
  const ref = await addDoc(col, {
    ...trackData,
    order: maxOrder + 1,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export function deleteTrack(uid, playlistId, trackId) {
  assertRequired('deleteTrack.uid', uid);
  assertRequired('deleteTrack.playlistId', playlistId);
  assertRequired('deleteTrack.trackId', trackId);
  return deleteDoc(safeDoc(db, 'users', uid, 'playlists', playlistId, 'tracks', trackId));
}

export function updatePlaylist(uid, playlistId, data) {
  assertRequired('updatePlaylist.uid', uid);
  assertRequired('updatePlaylist.playlistId', playlistId);
  return updateDoc(safeDoc(db, 'users', uid, 'playlists', playlistId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePlaylist(uid, playlistId) {
  assertRequired('deletePlaylist.uid', uid);
  assertRequired('deletePlaylist.playlistId', playlistId);
  await deleteDoc(safeDoc(db, 'users', uid, 'playlists', playlistId));
}

export function updateTrack(uid, playlistId, trackId, patch) {
  assertRequired('updateTrack.uid', uid);
  assertRequired('updateTrack.playlistId', playlistId);
  assertRequired('updateTrack.trackId', trackId);
  return updateDoc(
    safeDoc(db, 'users', uid, 'playlists', playlistId, 'tracks', trackId),
    {
      ...patch,
      updatedAt: serverTimestamp(),
    }
  );
}

export async function reorderTracks(uid, playlistId, orderedIds) {
  assertRequired('reorderTracks.uid', uid);
  assertRequired('reorderTracks.playlistId', playlistId);
  if (!Array.isArray(orderedIds)) throw new Error('reorderTracks: orderedIds must be array');

  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i];
    batch.set(
      safeDoc(db, 'users', uid, 'playlists', playlistId, 'tracks', id),
      { order: i, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
  await batch.commit();
}

export async function moveTrack(uid, fromPid, toPid, trackId) {
  assertRequired('moveTrack.uid', uid);
  assertRequired('moveTrack.fromPid', fromPid);
  assertRequired('moveTrack.toPid', toPid);
  assertRequired('moveTrack.trackId', trackId);

  const fromRef = safeDoc(db, 'users', uid, 'playlists', fromPid, 'tracks', trackId);
  const snap = await getDoc(fromRef);
  if (!snap.exists()) return;

  const data = snap.data();
  const toCol = safeCollection(db, 'users', uid, 'playlists', toPid, 'tracks');
  const last = await getDocs(query(toCol, orderBy('order', 'desc')));
  const nextOrder = last.empty ? 0 : ((last.docs[0].data().order || 0) + 1);

  const batch = writeBatch(db);
  const newRef = safeAutoDoc(toCol);
  batch.set(newRef, {
    ...data,
    order: nextOrder,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    fromPlaylistId: fromPid,
    movedAt: serverTimestamp(),
  });
  batch.delete(fromRef);
  await batch.commit();

  return newRef.id;
}

export async function ensureAllPlaylist(uid) {
  assertRequired('ensureAllPlaylist.uid', uid);
  const plsCol = safeCollection(db, 'users', uid, 'playlists');

  const snap = await getDocs(query(plsCol, orderBy('order', 'asc')));

  let maxOrder = -1;
  const alls = [];
  snap.docs.forEach(d => {
    const data = d.data();
    maxOrder = Math.max(maxOrder, data.order ?? 0);
    if ((data.name || '').toLowerCase() === 'all') {
      alls.push({ id: d.id, ...data });
    }
  });

  if (alls.length === 1) return alls[0].id;

  const batch = writeBatch(db);

  if (alls.length === 0) {
    const newRef = safeAutoDoc(plsCol);
    batch.set(newRef, {
      name: 'All',
      order: maxOrder + 1,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
    return newRef.id;
  }

  const keep = alls[0];
  for (let i = 1; i < alls.length; i++) {
    batch.delete(safeDoc(db, 'users', uid, 'playlists', alls[i].id));
  }
  await batch.commit();
  return keep.id;
}

export async function deletePlaylistKeepTracks(uid, playlistId, toPlaylistId) {
  assertRequired('deletePlaylistKeepTracks.uid', uid);
  assertRequired('deletePlaylistKeepTracks.playlistId', playlistId);

  const targetPid = toPlaylistId || (await ensureAllPlaylist(uid));

  const fromCol = safeCollection(db, 'users', uid, 'playlists', playlistId, 'tracks');
  const toCol = safeCollection(db, 'users', uid, 'playlists', targetPid, 'tracks');

  const [fromSnap, lastSnap] = await Promise.all([
    getDocs(query(fromCol, orderBy('order', 'asc'))),
    getDocs(query(toCol, orderBy('order', 'desc'))),
  ]);

  let nextOrder = lastSnap.docs.length ? ((lastSnap.docs[0].data().order || 0) + 1) : 0;

  const batch = writeBatch(db);
  fromSnap.docs.forEach(d => {
    const data = d.data();
    const newRef = safeAutoDoc(toCol);
    batch.set(newRef, {
      ...data,
      order: nextOrder++,
      fromPlaylistId: playlistId,
      movedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.delete(d.ref);
  });

  batch.delete(safeDoc(db, 'users', uid, 'playlists', playlistId));
  await batch.commit();

  return targetPid;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Lex AI Newsroom
   ──────────────────────────────────────────────────────────────────────────────── */
const newsroomPublicCol   = () => safeCollection(db, 'users', 'public', 'lexAiNewsroom');
const newsroomUserCol     = (uid) => safeCollection(db, 'users', uid, 'lexAiNewsroom');
const newsroomSavedCol    = (uid) => safeCollection(db, 'users', uid, 'savedNewsroom');

export async function publishArticle(uid, data) {
  assertRequired('publishArticle.uid', uid);
  const payload = {
    title: (data && data.title) || '',
    content: (data && data.content) || '',
    tags: (data && data.tags) || '',
    thumbnail: (data && data.thumbnail) || '',
    authorName: (data && data.authorName) || 'Anonymous',
    excerpt: (data && data.excerpt) || '',
    isPublic: true,
    sourceUid: uid,
    likes: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const privRef = await addDoc(newsroomUserCol(uid), payload);
  const pubRef  = await addDoc(newsroomPublicCol(), { ...payload, originalId: privRef.id });

  await setDoc(
    safeDoc(db, 'users', uid, 'lexAiNewsroom', privRef.id),
    { publicId: pubRef.id, updatedAt: serverTimestamp() },
    { merge: true }
  );

  return { privateId: privRef.id, publicId: pubRef.id };
}

export async function updateArticleBoth(uid, privateId, publicId, patch) {
  assertRequired('updateArticleBoth.uid', uid);
  assertRequired('updateArticleBoth.privateId', privateId);
  assertRequired('updateArticleBoth.publicId', publicId);

  const data = { ...patch, updatedAt: serverTimestamp() };
  await Promise.all([
    setDoc(safeDoc(db, 'users', uid, 'lexAiNewsroom', privateId), data, { merge: true }),
    setDoc(safeDoc(db, 'users', 'public', 'lexAiNewsroom', publicId), data, { merge: true }),
  ]);
  return true;
}

export async function deleteArticleEverywhere(uid, privateId, publicId) {
  assertRequired('deleteArticleEverywhere.uid', uid);
  assertRequired('deleteArticleEverywhere.privateId', privateId);
  assertRequired('deleteArticleEverywhere.publicId', publicId);
  await Promise.all([
    deleteDoc(safeDoc(db, 'users', uid, 'lexAiNewsroom', privateId)),
    deleteDoc(safeDoc(db, 'users', 'public', 'lexAiNewsroom', publicId)),
  ]);
  return true;
}

export async function saveToCollections(uid, publicArticle) {
  assertRequired('saveToCollections.uid', uid);
  const publicId = publicArticle && (publicArticle.id || publicArticle.publicId);
  assertRequired('saveToCollections.publicId', publicId);

  const tagsStr =
    Array.isArray(publicArticle && publicArticle.tags)
      ? publicArticle.tags.join(', ')
      : (publicArticle && publicArticle.tags) || '';

  const payload = {
    publicId: publicId,
    title: (publicArticle && publicArticle.title) || '',
    authorName: (publicArticle && publicArticle.authorName) || '',
    createdAt: (publicArticle && publicArticle.createdAt) || null,
    tags: tagsStr,
    excerpt: (publicArticle && publicArticle.excerpt) || '',
    thumbnail: (publicArticle && publicArticle.thumbnail) || '',
    savedAt: serverTimestamp(),
  };

  await setDoc(safeDoc(db, 'users', uid, 'savedNewsroom', publicId), payload, { merge: true });
  return true;
}

export async function unsaveFromCollections(uid, publicId) {
  assertRequired('unsaveFromCollections.uid', uid);
  assertRequired('unsaveFromCollections.publicId', publicId);
  await deleteDoc(safeDoc(db, 'users', uid, 'savedNewsroom', publicId));
  return true;
}

export async function listPublicArticles(limitCount) {
  const qRef = query(newsroomPublicCol(), orderBy('createdAt', 'desc'));
  const snap = await getDocs(qRef);
  const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return typeof limitCount === 'number' ? all.slice(0, limitCount) : all;
}

export async function getPublicArticle(publicId) {
  assertRequired('getPublicArticle.publicId', publicId);
  const snap = await getDoc(safeDoc(db, 'users', 'public', 'lexAiNewsroom', publicId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function listenMyArticles(uid, callback) {
  if (!uid) { console.error('listenMyArticles: uid missing'); return () => {}; }
  const qRef = query(newsroomPublicCol(), orderBy('createdAt', 'desc'));
  return onSnapshot(qRef, snap => {
    const list = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(x => x.sourceUid === uid);
    callback(list);
  });
}

export function listenMyCollections(uid, callback) {
  if (!uid) { console.error('listenMyCollections: uid missing'); return () => {}; }
  const col = newsroomSavedCol(uid);
  const unsub = onSnapshot(col, snap => {
    const list = snap.docs.map(d => ({ id: d.id, publicId: d.id, ...d.data() }));
    list.sort((a, b) => (b.savedAt?.seconds || 0) - (a.savedAt?.seconds || 0));
    callback(list);
  });
  return unsub;
}

export async function isSaved(uid, publicId) {
  assertRequired('isSaved.uid', uid);
  assertRequired('isSaved.publicId', publicId);
  const snap = await getDoc(safeDoc(db, 'users', uid, 'savedNewsroom', publicId));
  return snap.exists();
}

/* ────────────────────────────────────────────────────────────────────────────────
   Open Calendar (serverless, Firestore)
──────────────────────────────────────────────────────────────────────────────── */
const openCalEventsCol = () => safeCollection(db, 'openCalendarEvents');

export async function listOpenCalPublicEvents() {
  const snap = await getDocs(query(openCalEventsCol(), orderBy('startAt', 'asc')));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(e => (e.visibility || 'public') !== 'private' && (e.status || 'draft') !== 'archived');
}

export async function upsertOpenCalEvent(ev) {
  if (!ev || !ev.id) throw new Error('upsertOpenCalEvent.id is required');
  await setDoc(
    safeDoc(db, 'openCalendarEvents', ev.id),
    { ...ev, updatedAt: serverTimestamp() },
    { merge: true }
  );
  return ev.id;
}

export async function deleteOpenCalEvent(id) {
  if (!id) throw new Error('deleteOpenCalEvent.id is required');
  return deleteDoc(safeDoc(db, 'openCalendarEvents', id));
}

export function listenOpenCalOrgEvents(orgId, callback) {
  if (!orgId) { console.error('listenOpenCalOrgEvents: orgId missing'); return () => {}; }
  const qRef = query(openCalEventsCol(), where('org.id', '==', orgId), orderBy('startAt', 'asc'));
  return onSnapshot(qRef, snap => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  });
}
